import { useEffect, useRef, TransitionEvent, useState } from 'react';
import classNames from 'classnames';

import { IS_DISCORD_ACTIVITY } from '@/globals';
import { useTheme } from '@/theme';
import { getAvailableBadges, getAvailableTitles, loadCurrentUserProfileV2 } from '@/state/features/profile';
import {
    isDiscordOnMobile,
    shareImageInDiscordDesktop,
    triggerDiscordShare,
} from '@/components/DiscordAuthFlow/useDiscord';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { closeProfile } from '@/state/features/app';
import { useAudio } from '@/hooks/audio';
import IconButton from '@/components/IconButton';
import Icon, { IconTypes } from '@/components/Icon';
import { sendEventV2 } from '@/utils/analytics';
import { Badge, Title } from '@/models/profile';

import LevelBadge from './LevelBadge';
import RoundProgressBar from './RoundProgressBar';
import ShinyLoader from './ShinyLoader';
import TitleBadge from './Title';
import getProfileV2ShareImage from './imageMaker';
import * as styles from './styles.module.scss';

export default function ProfileDrawerV2() {
    const dispatch = useAppDispatch();
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const { audio } = useAudio();
    const theme = useTheme();

    const isProfileOpen = useAppSelector((state) => state.app.isProfileOpen);
    const { currentUserProfileV2, availableBadges, availableTitles } = useAppSelector((state) => state.profile);
    const loggedIn = useAppSelector((state) => state.auth.loggedIn);

    const brandingData = theme.customJSON.profileDrawerV2;
    const shareText = theme.customJSON.informationDrawer?.shareButton?.shareText;
    // const isDiscordBranded = IS_DISCORD_ACTIVITY;
    const isDiscordBranded = true; // No web versions currently so leave as discord branded

    useEffect(() => {
        if (isProfileOpen) {
            audio?.play('gust-open');
            sendEventV2('open_profile');
        }
    }, [isProfileOpen]);

    // Move focus to drawer when opened
    function onTransitionEnd(e: TransitionEvent<HTMLDivElement>) {
        if (isProfileOpen && e.target === e.currentTarget) {
            setTimeout(() => {
                closeButtonRef.current?.focus();
            }, 200);
        }
    }

    useEffect(() => {
        // Load data initially
        dispatch(getAvailableBadges());
        dispatch(getAvailableTitles());
    }, [loggedIn]);

    useEffect(() => {
        // Load data initially, and then again once user is logged in
        dispatch(loadCurrentUserProfileV2());
    }, [loggedIn]);

    return (
        <div
            className={classNames(styles.ProfileDrawer, {
                [styles['isOpen']]: isProfileOpen,
            })}
            inert={!isProfileOpen ? '' : undefined}
            onTransitionEnd={onTransitionEnd}
        >
            <IconButton
                buttonRef={closeButtonRef}
                iconType="close"
                buttonStyle="round"
                ariaLabel="Close Profile"
                className={styles.closeButton}
                onClick={() => {
                    audio?.play('gust-close');
                    sendEventV2('close_profile');
                    dispatch(closeProfile());
                }}
            />
            <ProfileDrawerV2Contents
                isVisible={isProfileOpen}
                availableTitles={availableTitles}
                usersTitles={currentUserProfileV2?.titles}
                availableBadges={availableBadges}
                usersBadges={currentUserProfileV2?.badges}
                hasGameCompletionUI={brandingData?.hasGameCompletionUI !== false} // defaults to true
                isGameCompleted={currentUserProfileV2?.missionCompleted}
                username={currentUserProfileV2?.displayName}
                brandCardImage={brandingData?.cardImageUrl}
                brandLogo={brandingData?.logoImageUrl}
                brandBackgroundImage={brandingData?.backgroundImageUrl}
                platformIcon={isDiscordBranded ? 'discord' : 'profile'}
                gameDescriptionWord={isDiscordBranded ? 'Quest' : 'Mission'}
                gameIcon={isDiscordBranded ? 'wreath' : 'action'}
                hideBadgesLine={brandingData?.hideBadgesLine}
                shareText={shareText}
            />
        </div>
    );
}

interface ProfileDrawerV2ContentsProps {
    isVisible?: boolean; // For triggering animations on open
    availableTitles: Title[];
    usersTitles: Title[];
    availableBadges: Badge[];
    usersBadges: Badge[];
    username: string;
    brandCardImage: string;
    brandLogo: string;
    brandBackgroundImage: string;
    hasGameCompletionUI?: boolean; // e.g 'Quest complete' text is visible
    isGameCompleted?: boolean;
    platformIcon: IconTypes; // 'wreath'
    gameDescriptionWord: string; // 'Mission'
    gameIcon: IconTypes; // 'discord'
    hideBadgesLine?: boolean; // Hides the line behind the badges
    shareText: string; // Used when sharing on discord mobile as image sharing not supported
}

function ProfileDrawerV2Contents({
    isVisible,
    availableTitles,
    usersTitles,
    availableBadges,
    usersBadges,
    username,
    brandCardImage,
    brandLogo,
    brandBackgroundImage,
    hasGameCompletionUI = true,
    isGameCompleted,
    platformIcon,
    gameDescriptionWord,
    gameIcon,
    hideBadgesLine,
    shareText,
}: ProfileDrawerV2ContentsProps) {
    const [currentTab, setCurrentTab] = useState<'levels' | 'titles'>('levels');
    const [isShareError, setIsShareError] = useState(false);
    const [isShareLoading, setIsShareLoading] = useState(false);

    const [revealedHints, setRevealedHints] = useState<Set<string>>(new Set());
    const handleRevealHint = (id: string) => {
        setRevealedHints((prev) => new Set(prev).add(id));
        sendEventV2('profile_view_hint', [id]);
    };

    const COMPLETED_COLOR = '#7FBF5A';
    // Current badge is badge with highest order number (e.g. gold with order of 2 is shown over bronze with 0)
    const currentBadge = usersBadges.length ? usersBadges.reduce((a, b) => (b.order > a.order ? b : a)) : null;
    const levelColor = currentBadge?.color ?? 'white';
    const gameStatusText = isGameCompleted ? 'Complete' : 'Incomplete';
    const numberOfCompletedTitles = usersTitles?.length || 0;
    const numberOfAvailableTitles = availableTitles?.length || 0;
    const titlesPercent = (numberOfCompletedTitles / numberOfAvailableTitles) * 100 || 0;

    async function openProfileImage() {
        setIsShareLoading(true);
        setIsShareError(false);
        try {
            const filename = `${gameDescriptionWord} Scorecard.png`;

            // Give 2 extra seconds to allow device sharing functions to load
            const ADDITIONAL_LOADING_TIME_MS = 2000;

            if (IS_DISCORD_ACTIVITY) {
                if (isDiscordOnMobile()) {
                    // Don't share the card on discord mobile as not supported
                    await triggerDiscordShare(shareText);
                    setTimeout(() => {
                        setIsShareLoading(false);
                    }, ADDITIONAL_LOADING_TIME_MS);
                } else {
                    // Discord desktop
                    const imageUrl = await getProfileV2ShareImage(brandBackgroundImage);
                    await shareImageInDiscordDesktop(imageUrl, filename);
                    sendEventV2('share_profile_image', ['discord']);

                    setTimeout(() => {
                        setIsShareLoading(false);
                    }, ADDITIONAL_LOADING_TIME_MS);
                }
            } else {
                // Web
                const imageUrl = await getProfileV2ShareImage(brandBackgroundImage);
                sendEventV2('share_profile_image', ['web']);
                // Does not work in iPhone browser
                window.open(imageUrl, '_blank', 'noopener');

                setTimeout(() => {
                    setIsShareLoading(false);
                }, ADDITIONAL_LOADING_TIME_MS);
            }
        } catch (error) {
            console.log('Error sharing profile:', error);
            setIsShareError(true);
            setIsShareLoading(false);
        }
    }

    return (
        <div className={styles.scrollContainer}>
            <div className={styles.content}>
                <div
                    className={styles.pageBgImage}
                    style={{
                        backgroundImage: `url("${brandBackgroundImage}")`,
                    }}
                ></div>
                <section className={styles.scorecard} id="arise-profile-scorecard">
                    <div className={styles.scorecardContent}>
                        <div
                            className={styles.bgImage}
                            style={{
                                backgroundImage: `url("${brandCardImage}")`,
                            }}
                        ></div>
                        <div className={styles.divider} />
                        <div className={styles.topBar}>
                            {brandLogo && <img src={brandLogo} className={styles.brandLogo} crossOrigin="anonymous" />}
                            <Icon className={styles.platformLogo} type={platformIcon} />
                        </div>
                        <div className={styles.titleBar}>
                            <h2>{gameDescriptionWord} Scorecard</h2>
                            {username && (
                                <div className={styles.username}>
                                    <Icon className={styles.icon} type={platformIcon} />
                                    <span>{username}</span>
                                </div>
                            )}
                        </div>
                        <div className={styles.divider} />
                        <ul className={styles.statBoxContainer}>
                            {hasGameCompletionUI && (
                                <li className={styles.statBox}>
                                    <ShinyLoader isLoaded={typeof isGameCompleted === 'boolean'} />
                                    <Icon className={styles.icon} type={gameIcon} />
                                    <div className={styles.statTextContainer}>
                                        <span className={styles.statTitle}>{gameDescriptionWord}</span>
                                        <span className={styles.subtitle}>{gameStatusText}</span>
                                    </div>
                                </li>
                            )}
                            <li className={styles.statBox}>
                                <ShinyLoader
                                    isLoaded={
                                        typeof numberOfCompletedTitles === 'number' &&
                                        typeof numberOfAvailableTitles === 'number'
                                    }
                                />
                                <Icon className={styles.icon} type="crown" />
                                <div className={styles.statTextContainer}>
                                    <span className={styles.statTitle}>
                                        {numberOfCompletedTitles}/{numberOfAvailableTitles}
                                    </span>
                                    <span className={styles.subtitle}>Titles</span>
                                </div>
                            </li>
                            <li className={classNames(styles.statBox, styles.statBoxLong)}>
                                <ShinyLoader isLoaded={!!usersBadges} />
                                <Icon
                                    className={classNames(styles.icon, styles.active)}
                                    type="shield"
                                    style={{ fill: levelColor }}
                                />
                                <div className={styles.statTextContainer}>
                                    <span className={styles.subtitle}>Level:</span>
                                    <span className={styles.statTitle} style={{ color: levelColor }}>
                                        {currentBadge?.name ?? 'Incomplete'}
                                    </span>
                                </div>
                            </li>
                            <LevelBadge
                                classname={styles.levelBadge}
                                imagePath={currentBadge?.imagePath}
                                isLocked={!currentBadge}
                                isLoading={!usersBadges}
                                shimmerDelay={3}
                            />
                        </ul>
                        <div className={styles.divider} />
                    </div>
                </section>
                <div className={styles.ctaButtonsContainer}>
                    <button onClick={openProfileImage} disabled={isShareLoading}>
                        Share
                        <Icon type={isShareLoading ? 'animated-spinner' : 'share'} />
                    </button>
                    {isShareError && <p className={styles.shareError}>Unable to share, try again later</p>}
                </div>
                <section className={styles.yourStats}>
                    <h2>
                        <Icon className={classNames(styles.icon)} type="bolt" />
                        Your stats
                    </h2>
                    <div className={styles.gridContainer}>
                        <div className={classNames(styles.level, styles.box)}>
                            <ShinyLoader isLoaded={!!usersBadges} />
                            <LevelBadge
                                classname={styles.levelBadge}
                                imagePath={currentBadge?.imagePath}
                                isLoading={!usersBadges}
                                shimmerDelay={4}
                                isLocked={!currentBadge}
                            />
                            <span className={styles.statTitle} style={{ color: levelColor }}>
                                {currentBadge?.name ?? 'Incomplete'}
                            </span>
                            <span className={styles.subtitle}>Your Level</span>
                        </div>
                        {hasGameCompletionUI && (
                            <div className={classNames(styles.statBox, styles.box)}>
                                <ShinyLoader isLoaded={typeof isGameCompleted === 'boolean'} />
                                <div className={styles.left}>
                                    <Icon
                                        className={classNames(styles.icon, styles.active)}
                                        type={gameIcon}
                                        style={{ fill: isGameCompleted ? COMPLETED_COLOR : '#BF5A5A' }}
                                    />
                                </div>
                                <div>
                                    <span className={styles.statTitle}>{gameDescriptionWord}</span>
                                    <span className={styles.subtitle}>{gameStatusText}</span>
                                </div>
                            </div>
                        )}
                        <div className={classNames(styles.statBox, styles.box)}>
                            <ShinyLoader
                                isLoaded={
                                    typeof numberOfCompletedTitles === 'number' &&
                                    typeof numberOfAvailableTitles === 'number'
                                }
                            />
                            <div className={styles.left}>
                                <RoundProgressBar
                                    percent={isVisible ? titlesPercent : 100}
                                    className={styles.progress}
                                    icon="crown"
                                />
                            </div>
                            <div>
                                <span className={styles.titlesNumber}>
                                    <span className={styles.highlight}>{numberOfCompletedTitles}</span>
                                    <span>/{numberOfAvailableTitles}</span>
                                </span>
                                <span className={styles.subtitle}>Titles Unlocked</span>
                            </div>
                        </div>
                    </div>
                </section>
                <section className={styles.tabGroup}>
                    <div className={styles.tabButtons} role="tablist" aria-label="Stat Information Options">
                        <button
                            className={classNames({ [styles.active]: currentTab === 'levels' })}
                            role="tab"
                            aria-selected={currentTab === 'levels'}
                            disabled={currentTab === 'levels'}
                            aria-controls="panel-levels"
                            id="tab-levels"
                            onClick={() => {
                                setCurrentTab('levels');
                                sendEventV2('view_profile_section', ['levels']);
                            }}
                        >
                            <Icon type="shield" />
                            Levels
                        </button>
                        <button
                            className={classNames({ [styles.active]: currentTab === 'titles' })}
                            role="tab"
                            aria-selected={currentTab === 'titles'}
                            disabled={currentTab === 'titles'}
                            aria-controls="panel-titles"
                            id="tab-titles"
                            onClick={() => {
                                setCurrentTab('titles');
                                sendEventV2('view_profile_section', ['titles']);
                            }}
                        >
                            <Icon type="crown" />
                            Titles
                        </button>
                    </div>
                    {currentTab === 'levels' && (
                        <section id="panel-levels" role="tabpanel" aria-labelledby="tab-levels">
                            <ul className={styles.levelInfoBoxes}>
                                <ShinyLoader
                                    isLoaded={availableBadges && !!usersBadges}
                                    className={styles.tabGroupLoader}
                                />
                                {availableBadges &&
                                    usersBadges &&
                                    availableBadges?.map((badge: Badge, index) => {
                                        const badgeIsCompleted = usersBadges?.some(
                                            (userBadge) => userBadge.id === badge.id,
                                        );
                                        return (
                                            <li
                                                key={badge.id}
                                                className={classNames(styles.levelInfoBox, {
                                                    [styles.hasLine]: !hideBadgesLine,
                                                })}
                                            >
                                                <LevelBadge
                                                    classname={styles.levelBadge}
                                                    imagePath={badge.imagePath}
                                                    isLoading={!usersBadges}
                                                    shimmerDelay={index}
                                                    isLocked={!badgeIsCompleted}
                                                />
                                                <div className={styles.textContainer}>
                                                    <span className={styles.largerText} style={{ color: badge.color }}>
                                                        {badge.name}
                                                    </span>
                                                    <span className={styles.subtitle}>{badge.description}</span>
                                                </div>
                                                <Icon
                                                    className={styles.icon}
                                                    type="checked-box"
                                                    style={{
                                                        fill: badgeIsCompleted ? COMPLETED_COLOR : '#4E4E4E',
                                                    }}
                                                />
                                            </li>
                                        );
                                    })}
                            </ul>
                        </section>
                    )}
                    {currentTab === 'titles' && (
                        <section id="panel-titles" role="tabpanel" aria-labelledby="tab-titles">
                            <ul className={styles.titles}>
                                <ShinyLoader isLoaded={!!availableTitles} className={styles.tabGroupLoader} />
                                {availableTitles?.map((title: Title) => {
                                    const titleIsCompleted = usersTitles?.some(
                                        (userBadge) => userBadge.id === title.id,
                                    );
                                    return (
                                        <li key={title.id}>
                                            {titleIsCompleted ? (
                                                <>
                                                    <div className={styles.left}>
                                                        <TitleBadge text={title.title} rarity={title.rarity} />
                                                    </div>
                                                    <span className={styles.subtitle}>
                                                        <span className={styles[title.rarity?.toLowerCase()]}>
                                                            {title.rarity}:{' '}
                                                        </span>
                                                        <span>{title.description}</span>
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <div className={styles.left}>
                                                        <Icon className={styles.lockedIcon} type="locked" />
                                                    </div>
                                                    <span className={styles.subtitle}>
                                                        {revealedHints.has(title.id)
                                                            ? title.hint
                                                            : 'You haven’t found this Title yet!'}
                                                    </span>
                                                    {!revealedHints.has(title.id) && (
                                                        <button
                                                            onClick={() => handleRevealHint(title.id)}
                                                            className={styles.hintButton}
                                                        >
                                                            Hint
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </section>
                    )}
                </section>
            </div>
        </div>
    );
}
